import { useMemo, useState } from "react";
import { ToolActivity } from "common/utils/chatAgent/types";
import { Icon as LucideIcon } from "../../../Common/Icons/LucideIcons";
import {
  Actions,
  Card,
  Details,
  ErrorText,
  FieldName,
  FieldRow,
  FieldValue,
  Header,
  Meta,
  SmallButton,
  StatusPill,
  Summary,
  Title,
  TitleRow,
} from "../__styles__/ToolActivityCard";

function flattenUpdates(
  updates: Record<string, any>,
  prefix = "",
): Array<{ path: string; value: any }> {
  const entries: Array<{ path: string; value: any }> = [];
  const isObject = (v: any) => v && typeof v === "object" && !Array.isArray(v);
  for (const [key, value] of Object.entries(updates || {})) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (isObject(value)) {
      entries.push(...flattenUpdates(value, path));
    } else {
      entries.push({ path, value });
    }
  }
  return entries;
}

function truncateString(str: string, max = 160): string {
  if (str.length <= max) return str;
  return `${str.slice(0, max)}…`;
}

function toDisplayValue(value: any): string {
  try {
    if (typeof value === "string") return truncateString(value);
    return truncateString(JSON.stringify(value));
  } catch {
    return String(value);
  }
}

export type FormToolActivityCardProps = {
  activity: ToolActivity;
};

/**
 * Tool activity card for the submission form agent (V1).
 * Displays form field updates with field count summary.
 */
const FormToolActivityCard = ({ activity }: FormToolActivityCardProps) => {
  const [expanded, setExpanded] = useState(false);
  const fields = useMemo(
    () => flattenUpdates(activity.result || {}),
    [activity.result],
  );
  const durationLabel =
    activity.durationMs != null ? `${activity.durationMs} ms` : undefined;

  const summary = useMemo(() => {
    if (activity.summary) {
      return activity.summary;
    }
    if (fields.length > 0) {
      return `Updated ${fields.length} field${fields.length === 1 ? "" : "s"}`;
    }
    return undefined;
  }, [activity.summary, fields.length]);

  const copyResult = async () => {
    try {
      await navigator.clipboard.writeText(
        JSON.stringify(activity.result ?? {}, null, 2),
      );
    } catch (_) {
      // no-op
    }
  };

  return (
    <Card aria-live="polite">
      <Header>
        <TitleRow>
          <LucideIcon iconName="component" size={14} color="contentPrimary" />
          <Title>{activity.name || "Tool"}</Title>
          <StatusPill status={activity.status as any} role="status">
            {activity.status === "completed" && "Completed"}
            {activity.status === "running" && "Running"}
            {activity.status === "failed" && "Failed"}
          </StatusPill>
        </TitleRow>
        <Actions>
          <SmallButton
            onClick={() => setExpanded(prev => !prev)}
            aria-expanded={expanded}
            aria-controls={`tool-activity-details-${activity.startedAt}`}
          >
            {expanded ? "Hide details" : "Show details"}
          </SmallButton>
          <SmallButton onClick={copyResult} disabled={!activity.result}>
            Copy
          </SmallButton>
        </Actions>
      </Header>

      {summary && <Summary>{summary}</Summary>}
      {activity.status === "failed" && activity.errorMessage && (
        <ErrorText>{activity.errorMessage}</ErrorText>
      )}
      <Meta>
        {durationLabel && (
          <>
            <LucideIcon iconName="clock" size={12} color="contentSecondary" />
            <span>{durationLabel}</span>
          </>
        )}
      </Meta>

      {expanded && (
        <Details id={`tool-activity-details-${activity.startedAt}`}>
          {fields.length === 0 ? (
            <FieldName>No changes</FieldName>
          ) : (
            fields.slice(0, 50).map(({ path, value }) => (
              <FieldRow key={path}>
                <FieldName>{path}</FieldName>
                <FieldValue>{toDisplayValue(value)}</FieldValue>
              </FieldRow>
            ))
          )}
          {fields.length > 50 && (
            <FieldName>{`…and ${fields.length - 50} more`}</FieldName>
          )}
        </Details>
      )}
    </Card>
  );
};

export default FormToolActivityCard;
